"use client";

import type { ArticleRef } from "@ellaandil/formatters";
import { useInfiniteQuery } from "@tanstack/react-query";
import { classed } from "@trydig/ui";
import { useQueryState } from "nuqs";
import type { ReactNode } from "react";
import { rpc } from "#rpc";
import { ArticleCard, ArticleCardSkeleton } from "@/components/ui/ArticleCard";
import { Button } from "@/components/ui/Button";
import {
	ARTICLES_FILTER_TAG,
	ARTICLES_PER_PAGE,
} from "@/features/articles/ArticleListingPage/config";
import { useTranslations } from "@/i18n/client";

const MOBILE_COLUMNS = 2;
const DESKTOP_COLUMNS = 3;

export interface ArticleListProps {
	storySlug: string;
	initialArticles: ArticleRef[];
	total: number;
	content: ReactNode[];
}

export const ArticleList = ({ storySlug, initialArticles, total, content }: ArticleListProps) => {
	const t = useTranslations("articles");
	const [selectedTag] = useQueryState(ARTICLES_FILTER_TAG);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, isLoading } =
		useInfiniteQuery({
			queryKey: ["articles", storySlug, selectedTag],
			queryFn: ({ pageParam = 1 }) =>
				rpc.stories.listArticles({
					storySlug,
					page: pageParam,
					tag: selectedTag,
				}),
			initialPageParam: 1,
			getNextPageParam: (lastPage, allPages) => {
				return lastPage.hasMore ? allPages.length + 1 : undefined;
			},
			initialData: !selectedTag
				? {
						pages: [
							{
								articles: initialArticles,
								total,
								hasMore: initialArticles.length < total,
							},
						],
						pageParams: [1],
					}
				: undefined,
		});

	const allArticles = data?.pages.flatMap((page) => page.articles) ?? [];
	const totalArticles = data?.pages[data.pages.length - 1]?.total ?? 0;
	const remainingArticles = totalArticles - allArticles.length;

	const visibleContentBloks = content.filter((_, i) => {
		const mobileRowStart = i * 2 + 2;
		const desktopRowStart = i * 2 + 2;

		const requiredForMobile = (mobileRowStart - 1) * MOBILE_COLUMNS;
		const requiredForDesktop = (desktopRowStart - 1) * DESKTOP_COLUMNS + 1;

		let adjustedRequiredMobile = requiredForMobile;
		let adjustedRequiredDesktop = requiredForDesktop;

		for (let j = 0; j < i; j++) {
			const prevMobileRowStart = j * 2 + 2;
			const prevDesktopRowStart = j * 2 + 2;

			if (prevMobileRowStart < mobileRowStart) {
				adjustedRequiredMobile -= 2;
			}
			if (prevDesktopRowStart < desktopRowStart) {
				adjustedRequiredDesktop -= 2;
			}
		}

		const requiredArticles = Math.max(adjustedRequiredMobile, adjustedRequiredDesktop);

		return allArticles.length >= requiredArticles;
	});

	return (
		<>
			<Grid>
				{allArticles.map((article, index) => (
					<ArticleCard {...article} key={index} />
				))}
				{(isFetchingNextPage || isFetching || isLoading) &&
					Array.from({ length: ARTICLES_PER_PAGE }).map((_, i) => <ArticleCardSkeleton key={i} />)}
				{visibleContentBloks}
			</Grid>

			{hasNextPage && (
				<div className="px-grid-section-vertical-lg flex justify-center">
					<Button
						size="large"
						onClick={() => fetchNextPage()}
						className="mb-grid-section-vertical-lg w-full lg:w-auto"
						toolTip={remainingArticles}
						loading={isFetchingNextPage}
					>
						{t("loadMore")}
					</Button>
				</div>
			)}
		</>
	);
};

const Grid = classed.section(
	"grid grid-cols-2 lg:grid-cols-3 p-grid-section-vertical-lg gap-x-2 gap-y-grid-component-spacing-xl auto-rows-fr",
);

export const ArticleListSkeleton = ({ size = ARTICLES_PER_PAGE }: { size?: number }) => {
	return (
		<Grid>
			{Array.from({ length: size }).map((_, i) => (
				<ArticleCardSkeleton key={i} />
			))}
		</Grid>
	);
};
